import { Action, UnknownAction } from '@reduxjs/toolkit';
import { hasPath, pickBy } from 'ramda';
import {
  StateObservable,
  combineEpics,
} from 'redux-observable';
import {
  EMPTY, Observable,
  fromEvent, of,
} from 'rxjs';
import {
  debounceTime,
  filter,
  first,
  ignoreElements,
  map,
  mergeMap,
  skipUntil,
  switchMap,
  tap,
} from 'rxjs/operators';

import { EvoPlayer } from '@ezugi/bootstrap/src/views/GameVideo/EvoPlayer/player';
import {
  Howl, Howler, HowlerGlobal, HowlOptions,
} from '@ezugi/howler';
import baseSpriteJson from '@ezugi/static/sounds/base_sprite.json';
import { DeviceInfo, ls } from '@ezugi/utils';

import { MEDIA } from '../../../constants';
import { updateVideoVolume } from '../../../util/mediaUtils';
import {
  appActions,
  betActions,
  dialogActions,
  gameActions,
  mediaActions,
  roundActions,
  socketActions,
  soundActions,
  videoActions,
} from '../../actions';
import {
  backgroundMusicConfigSelector,
  backgroundMusicSoundSelector,
  currentLanguageSelector,
  gameSoundsSelector,
  isEvoPlayerSelector,
  isGameSoundMutedSelector,
  isGameStartedSelector,
  isVideoStreamSoundMutedSelector,
  volumeSelector,
} from '../../selectors';
import { socketConnectedSelector } from '../../selectors/socket';
import { RootState } from '../../types';
import playSound from './playSound';
import cache, {
  SoundCache,
  createAndCacheBackgroundMusic,
  createAndCacheSound,
  updateHowlerSettings,
} from './soundCache';

const VITE_STATIC_URL = import.meta.env.VITE_STATIC_URL;
const SOUNDS_URL = `${VITE_STATIC_URL}/sounds`;

const { volume, autoplaymute } = mediaActions;
const { app } = appActions;

const initEpic = (
  action$: Observable<UnknownAction>,
  state$: StateObservable<RootState>
): Observable<any> => action$.pipe(
  filter(app.start.match),
  first(),
  map(() => mediaActions.volume.set({ ...volumeSelector(state$.value) }))
);

// TODO: https://ezugi1.atlassian.net/browse/ERD-15299
// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare const window: {
  Howler: HowlerGlobal & { _howls: any[] }
} & Window;

export const preloadSoundsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
): Observable<any> => action$.pipe(
  filter(app.start.match),
  first(),
  tap(() => {
    createAndCacheSound({
      spriteName: 'base_sprite',
      spriteJson: baseSpriteJson as unknown as HowlOptions,
      sources: baseSpriteJson.src.map((name) => `${SOUNDS_URL}/${name}`),
    });

    const backgroundMusicSpriteJson = backgroundMusicConfigSelector(
      state$.value
    );

    if (!backgroundMusicSpriteJson) {
      return;
    }

    createAndCacheBackgroundMusic(backgroundMusicSpriteJson);
  }),
  ignoreElements()
);

const soundsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
): Observable<any> => action$.pipe(
  filter((action) => betActions.bet.set.match(action)
    || betActions.bet.clear.match(action)
    || betActions.bet.rebet.match(action)
    || betActions.bet.double.match(action)
    || betActions.history.pop.match(action)
    || roundActions.round.set.match(action)
    || dialogActions.dialog.add.match(action)),
  filter(() => isGameStartedSelector(state$.value) && !isGameSoundMutedSelector(state$.value)),
  debounceTime(0),
  tap((payload) => {
    playSound(payload, state$.value);
  }),
  ignoreElements()
);

const backgroundMusicEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
): Observable<any> => action$.pipe(
  filter(gameActions.game.start.match),
  tap(async () => {
    try {
      await cache.backgroundMusic?.play();
      if (!backgroundMusicSoundSelector(state$.value)) {
        await cache.backgroundMusic?.mute(true);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(
        'Error playing background music, please check configuration',
        err
      );
    }
  }),
  switchMap(() => fromEvent(document, 'visibilitychange').pipe(
    tap(() => {
      const documentVisibilityState = document.visibilityState === 'visible';
      cache.backgroundMusic?.mute(!(
        documentVisibilityState
        && socketConnectedSelector(state$.value)
        && backgroundMusicSoundSelector(state$.value)
      ));
    })
  )),
  ignoreElements()
);

const iosGameSoundsContextEpic = (
  _action$: Observable<Action>
): Observable<any> => fromEvent(document, 'visibilitychange').pipe(
  tap(() => {
    if (!DeviceInfo.isIOS) {
      return;
    }

    if (document.visibilityState === 'visible') {
      if (Howler.ctx.state === 'suspended') {
        setTimeout(() => {
          Howler.ctx.resume();
        }, 500);
      }
    } else {
      Howler.ctx.suspend();
    }
  }),
  ignoreElements()
);

const backgroundMusicStopEpic = (
  action$: Observable<Action>
): Observable<any> => action$.pipe(
  filter((action) => socketActions.socket.close.match(action) || socketActions.socket.failure.match(action)),
  tap(() => cache.backgroundMusic?.mute(true)),
  ignoreElements()
);

const backgroundMusicResumeEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
): Observable<any> => action$.pipe(
  filter(socketActions.socket.success.match),
  tap(() => cache.backgroundMusic?.mute(
    !backgroundMusicSoundSelector(state$.value)
  )),
  ignoreElements()
);

const soundPlayEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
): Observable<any> => action$.pipe(
  filter(soundActions.sound.play.match),
  filter(() => isGameStartedSelector(state$.value) && !isGameSoundMutedSelector(state$.value)),
  tap(({ payload }) => {
    const { code } = currentLanguageSelector(state$.value);
    return playSound({ meta: { ...payload, languageCode: code } }, state$.value);
  }),
  ignoreElements()
);

// TODO: Refactor Howler usage so that we don't need to access it's private internals as in the ._howls reference below
// https://ezugi1.atlassian.net/browse/ERD-15298
const soundStopEpic = (action$: Observable<Action>): Observable<any> => action$.pipe(
  filter(soundActions.sound.stop.match),
  tap(({ payload }: { payload: { soundName: string } }) => {
    try {
      if (!payload) {
        Howler.stop();
      } else {
        const { soundName } = payload;
        const sound: Howl | undefined = (Howler as any)?._howls?.find(
          (s: { _sprite: { [x: string]: any; }; }) => !!s._sprite[soundName]
        );
        sound?.stop();
      }
    } catch (_error) {}
  }),
  ignoreElements()
);

const gameSoundsSwitch = (stateValue: RootState): void => {
  const value = gameSoundsSelector(stateValue);
  const notBackgroundMusic = (_: any, key: keyof SoundCache): boolean => key !== 'backgroundMusic';
  const allSounds = pickBy<SoundCache, Howl[]>(notBackgroundMusic, cache);
  Object.values(allSounds).map((howl) => howl.mute(!value));
};

const backgroundMusicSwitch = (stateValue: RootState): void => {
  const value = backgroundMusicSoundSelector(stateValue);
  cache.backgroundMusic?.mute(!value);
};

const soundToggleEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
): Observable<any> => action$.pipe(
  filter(mediaActions.sound.toggle.match),
  tap(() => {
    gameSoundsSwitch(state$.value);
  }),
  ignoreElements()
);

const backgroundMusicToggleEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
): Observable<any> => action$.pipe(
  filter(mediaActions.music.toggle.match),
  tap(() => {
    backgroundMusicSwitch(state$.value);
  }),
  ignoreElements()
);

const volumeSetEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
): Observable<any> => action$.pipe(
  filter(mediaActions.volume.set.match),
  tap(() => {
    updateHowlerSettings(state$.value);
    const isVideoStreamMuted = isVideoStreamSoundMutedSelector(
      state$.value
    );
    const volumeState = volumeSelector(state$.value);

    let computedMutedValue = volumeState.muted;
    if (!volumeState.muted) {
      computedMutedValue = isVideoStreamMuted;
    }

    updateVideoVolume({ ...volumeState, muted: computedMutedValue });
  }),
  filter(() => !!isEvoPlayerSelector(state$.value)),
  mergeMap(() => {
    if (EvoPlayer?.canPlaySound) {
      return EMPTY;
    }
    return of(autoplaymute.set(true));
  })
);

// Update muted state if video player could not autoplay unmuted
const autoplayFailedEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
): Observable<any> => action$.pipe(
  filter(videoActions.video.set.match),
  filter(({ payload }) => hasPath([ 'warning', 'message' ], payload)),
  map(({ payload: { warning } }) => {
    const autoplayMuted = [ 'autoplay', 'failed' ].every((el) => warning.message.includes(el));
    const volumeState = volumeSelector(state$.value);
    return autoplaymute.set(autoplayMuted || volumeState.muted);
  })
);

const firstClickUnmuteEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>
): Observable<any> => fromEvent(document, 'click').pipe(
  skipUntil(action$.pipe(filter(videoActions.video.ready.match), first())),
  skipUntil(action$.pipe(filter(mediaActions.autoplaymute.set.match), first())),
  first(),
  mergeMap(() => {
    const volumeState = volumeSelector(state$.value);
    EvoPlayer.canPlaySound = true;
    const [ err, localStorageVolume ] = ls.get(MEDIA.VOLUME);
    return of(
      volume.set({
        ...volumeState,
        muted:
          err || DeviceInfo.isIOS
            ? false
            : localStorageVolume.muted,
      })
    );
  })
);

export default combineEpics(
  iosGameSoundsContextEpic,
  initEpic,
  backgroundMusicEpic,
  backgroundMusicStopEpic,
  backgroundMusicResumeEpic,
  backgroundMusicToggleEpic,
  soundsEpic,
  soundPlayEpic,
  soundToggleEpic,
  soundStopEpic,
  preloadSoundsEpic,
  autoplayFailedEpic,
  firstClickUnmuteEpic,
  volumeSetEpic
);
